import { useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { IMAGES } from "@/data/site";
import { STR } from "@/i18n";
import { PageHero, SectionHeader } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { toast } from "sonner";
import { Briefcase, Upload, Send, Loader2, MapPin, TrendingUp } from "lucide-react";

const OPENINGS_EN = STR.en.careersP.openings;

export default function Careers() {
  const { t } = useApp();
  const c = t.careersP;
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: OPENINGS_EN[0].title, cover: "" });
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (resume) fd.append("resume", resume);
      await api.post("/careers/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(c.success);
      setForm({ name: "", email: "", phone: "", position: OPENINGS_EN[0].title, cover: "" });
      setResume(null);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHero image={IMAGES.building} eyebrow={c.eyebrow} title={c.title} subtitle={c.sub} />

      <Section sid="careers.main" name="Openings & Application" className="oj-section">
        <div className="oj-container grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <SectionHeader center={false} eyebrow={c.openingsEyebrow} title={c.positions} />
            <div className="space-y-4">
              {c.openings.map((o, i) => {
                const enTitle = OPENINGS_EN[i].title;
                return (
                <Reveal key={enTitle} delay={i * 0.05}>
                  <button onClick={() => setForm((f) => ({ ...f, position: enTitle }))} data-testid={`career-opening-${i}`}
                    className={`w-full text-left oj-card p-5 flex items-center justify-between gap-4 ${form.position === enTitle ? "!border-amber-400/50" : ""}`}>
                    <div>
                      <h4 className="font-bold text-white">{o.title}</h4>
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-teal-400" /> {o.loc}</span>
                        <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5 text-teal-400" /> {o.type}</span>
                        <span className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-teal-400" /> {o.vertical}</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${form.position === enTitle ? "bg-amber-500 text-teal-950" : "bg-teal-50 text-teal-700 border border-teal-200"}`}>{form.position === enTitle ? c.selected : c.apply}</span>
                  </button>
                </Reveal>
                );
              })}
            </div>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="oj-card p-7 lg:sticky lg:top-24" data-testid="career-form">
              <h3 className="oj-h3 mb-6">{c.dropzone}</h3>
              <div className="space-y-4">
                <input required placeholder={c.ph.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="oj-input" data-testid="career-name" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input required type="email" placeholder={c.ph.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="oj-input" data-testid="career-email" />
                  <input placeholder={c.ph.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="oj-input" data-testid="career-phone" />
                </div>
                <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="oj-input" data-testid="career-position">
                  {c.openings.map((o, i) => <option key={OPENINGS_EN[i].title} value={OPENINGS_EN[i].title} className="bg-teal-950">{o.title}</option>)}
                </select>
                <textarea rows={4} placeholder={c.ph.cover} value={form.cover} onChange={(e) => setForm({ ...form, cover: e.target.value })} className="oj-input resize-none" data-testid="career-cover" />
                <label className="flex items-center gap-3 oj-input cursor-pointer" data-testid="career-resume-label">
                  <Upload className="w-5 h-5 text-teal-600 shrink-0" />
                  <span className="text-sm text-slate-600 truncate">{resume ? resume.name : c.upload}</span>
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files[0])} data-testid="career-resume-input" />
                </label>
                <button disabled={loading} className="oj-btn-gold w-full" data-testid="career-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {c.submitApp}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
